import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGE_SIZES } from "@/lib/imageSizes";

type Props = {
  imageUrls: Array<string>;
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const GalleryView: React.FC<Props> = ({
  currentIndex,
  imageUrls,
  isOpen,
  setIsOpen,
  setCurrentIndex,
}) => {
  const [api, setApi] = useState<CarouselApi>();

  const t = useTranslations("listing");

  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX !== null && touchEndX !== null) {
      const deltaX = touchStartX - touchEndX;

      const minSwipeDistance = 50;

      if (deltaX > minSwipeDistance) {
        onUpdateIndex("next");
      } else if (deltaX < -minSwipeDistance) {
        onUpdateIndex("prev");
      }
    }

    setTouchStartX(null);
    setTouchEndX(null);
  };

  const onUpdateIndex = (direction: "next" | "prev") => {
    const newIndex =
      direction === "next"
        ? (currentIndex + 1) % imageUrls.length
        : (currentIndex - 1 + imageUrls.length) % imageUrls.length;

    api?.scrollTo(newIndex);
    setCurrentIndex(newIndex);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        onUpdateIndex("prev");
      } else if (event.key === "ArrowRight") {
        onUpdateIndex("next");
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, api]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        closeButtonClassname="bg-white p-2"
        className="bg-transparent focus:outline-none border-transparent rounded-none p-5 max-w-full max-h-full h-full"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t("image-gallery")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 items-center justify-around">
          <p className="text-background font-bold text-xl">
            {currentIndex + 1} / {imageUrls.length}
          </p>

          <div className="h-full w-full grid grid-cols-2 grid-rows-[1fr_32px] md:grid-rows-1 md:grid-cols-[32px_1fr_32px] items-center gap-4 place-items-center">
            <Button
              aria-label={t("previous-image")}
              size="icon"
              onClick={() => onUpdateIndex("prev")}
              variant="secondary"
              className="col-start-1 row-start-2 row-end-3 md:row-start-1 md:row-end-2 col-end-2"
            >
              <ChevronLeft />
            </Button>
            <div className="w-full relative h-full col-start-1 md:col-start-2 row-start-1 row-end-2 col-end-3">
              <div
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className="absolute inset-0 flex items-center justify-center"
              >
                <Image
                  src={imageUrls[currentIndex]}
                  alt={t("image-alt", {
                    number: currentIndex + 1,
                    total: imageUrls.length,
                  })}
                  width={1920}
                  height={1080}
                  unoptimized
                  sizes={IMAGE_SIZES.hero}
                  className="h-full w-auto rounded-2xl object-contain"
                />
              </div>
            </div>
            <Button
              aria-label={t("next-image")}
              size="icon"
              onClick={() => onUpdateIndex("next")}
              variant="secondary"
              className="md:col-start-3 row-start-2 row-end-3 md:row-start-1 md:row-end-2 col-start-2 col-end-3 md:col-end-4"
            >
              <ChevronRight />
            </Button>
          </div>

          <div className="hidden md:block relative px-10 2xl:px-32">
            <Carousel setApi={setApi} className="w-full container">
              <CarouselContent>
                {imageUrls.map((url, index) => (
                  <CarouselItem
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className="basis-1/3 min-w-28 2xl:min-w-40 max-w-56 xl:basis-1/4 2xl:basis-1/5 flex items-center justify-center"
                  >
                    <Image
                      src={url}
                      alt={t("image-alt", {
                        number: index + 1,
                        total: imageUrls.length,
                      })}
                      width={400}
                      height={300}
                      unoptimized
                      sizes={IMAGE_SIZES.thumb200}
                      className="w-full h-32 object-cover rounded-2xl cursor-pointer"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GalleryView;
